import { makeAutoObservable, observableRef } from 'mobx';

import Sort from '@/common/state/Sort';
import { CrosstabAxis, NumericAxisSettings } from '@/product/types/CrosstabAxis';

export interface CrosstabCellCoordinates {
  rowId: string;
  columnId: string;
}

export interface CrosstabManualSortingAxes {
  rowAxis: CrosstabAxis;
  columnAxis: CrosstabAxis;
}

export class CrosstabSettingsModel {
  isActive?: Nullable<boolean>;
  rowAxis?: CrosstabAxis;
  columnAxis?: CrosstabAxis;
  rowSettings?: NumericAxisSettings;
  columnSettings?: NumericAxisSettings;
  isManualSorting?: Nullable<boolean>;
  // this is saved so if user changes axes with manual order of crosstab and than goes back,
  // we still use manual order
  manualSortingAxes?: CrosstabManualSortingAxes;
}

export default class CrosstabSettings {
  // should not be used directly, only from the ProductListViewStore
  isActive = false;

  rowAxis?: CrosstabAxis;

  columnAxis?: CrosstabAxis;

  rowSettings?: NumericAxisSettings;

  columnSettings?: NumericAxisSettings;

  selectedCell: Nullable<CrosstabCellCoordinates> = null;

  private isManualSortingEnabled = false;

  private boundManualSortingAxes?: CrosstabManualSortingAxes;

  constructor(model: CrosstabSettingsModel) {
    makeAutoObservable<CrosstabSettings, 'boundManualSortingAxes'>(this, {
      rowSettings: observableRef,
      columnSettings: observableRef,
      boundManualSortingAxes: observableRef,
    });

    this.isActive = !!model.isActive;
    this.rowAxis = model.rowAxis;
    this.columnAxis = model.columnAxis;
    this.rowSettings = model.rowSettings;
    this.columnSettings = model.columnSettings;
    this.isManualSortingEnabled = !!model.isManualSorting;
    this.boundManualSortingAxes = model.manualSortingAxes;
  }

  setIsActive = (isActive: boolean) => {
    this.isActive = isActive;

    if (!isActive) {
      this.setSelectedCell(null);
    }
  };

  setRowAxis = (axis: CrosstabAxis) => {
    if (axis !== this.rowAxis) {
      this.rowSettings = undefined;
    }

    this.rowAxis = axis;
  };

  setColumnAxis = (axis: CrosstabAxis) => {
    if (axis !== this.columnAxis) {
      this.columnSettings = undefined;
    }

    this.columnAxis = axis;
  };

  swapAxes = () => {
    [this.rowAxis, this.columnAxis] = [this.columnAxis, this.rowAxis];
    [this.rowSettings, this.columnSettings] = [this.columnSettings, this.rowSettings];
  };

  setRowSettings = (settings: NumericAxisSettings) => {
    this.rowSettings = settings;
  };

  setColumnSettings = (settings: NumericAxisSettings) => {
    this.columnSettings = settings;
  };

  setSelectedCell = (cell: Nullable<CrosstabCellCoordinates>) => {
    this.selectedCell = cell;
  };

  onSortChange = (sort: Sort) => {
    const isManualSortingEnabled = Boolean(sort.getFieldByName(Sort.SORT_MANUAL));

    if (isManualSortingEnabled === this.isManualSortingEnabled) {
      return;
    }

    this.isManualSortingEnabled = isManualSortingEnabled;
    this.boundManualSortingAxes = undefined;
  };

  bindManualSortingAxes = (axes: CrosstabManualSortingAxes) => {
    if (!this.isManualSortingEnabled || this.hasManualSortingForAxes(axes.rowAxis, axes.columnAxis)) {
      return;
    }

    this.boundManualSortingAxes = axes;
  };

  hasManualSortingForAxes(rowAxis: CrosstabAxis, columnAxis: CrosstabAxis): boolean {
    if (!this.isManualSortingEnabled || !this.boundManualSortingAxes) {
      return false;
    }

    const { rowAxis: manualRowAxis, columnAxis: manualColumnAxis } = this.boundManualSortingAxes;

    return (
      (manualRowAxis === rowAxis && manualColumnAxis === columnAxis) ||
      (manualRowAxis === columnAxis && manualColumnAxis === rowAxis)
    );
  }

  toModel(): Nullable<CrosstabSettingsModel> {
    return {
      isActive: this.isActive || null,
      rowAxis: this.rowAxis,
      columnAxis: this.columnAxis,
      rowSettings: this.rowSettings,
      columnSettings: this.columnSettings,
      isManualSorting: this.isManualSortingEnabled || null,
      manualSortingAxes: this.boundManualSortingAxes,
    };
  }
}
